import { ApplicationTokens } from '../catalog/application.tokens';
import { Configuration } from '../../configuration/api/configuration';
import { MessageBroker } from './enums';
import { IngressAdmissionConfig } from './ingress.admission';
import { MessageReliabilityProcessName } from './process.names';
import { OutboxMessage } from './outbox.message';

/**
 * Apps that dispatch processes with no explicit `scheduleOn`.
 * JMS is the default tenant app that owns shared processes registered
 * by shared modules (TenantDomainModule, WebHookModule, etc).
 * Processes owned by other apps (Connect, BGIS, ID) must declare
 * scheduleOn explicitly.
 */
export const DefaultScheduleOn: readonly ApplicationTokens[] = [
  ApplicationTokens.JMS,
] as const;

export interface MessageReliabilityRetentionConfig {
  successDays?: number;
  errorDays?: number;
  parkedDays?: number | null;
  /**
   * `false` keeps the tenant retention backfill (WBS-13348) off this process's rows. For a process whose
   * settled-looking rows can still be open, such as the planned-work readiness receipts: they are Delivered
   * from the moment they are claimed, carry no completion marker, and whether one is open is decided on its
   * source document, so a backfill stamp could expire an open receipt. Absent means backfilled.
   */
  backfill?: boolean;
}

export interface MessageReliabilityRateLimitConfig {
  /** Maximum tokens in the bucket. */
  maxTokens: number;
  /** Token refill interval in seconds. */
  refillRate: number;
  /** TTL for bucket cleanup in seconds. */
  ttlSeconds?: number;
}

export interface MessageReliabilityConcurrencyLimitConfig {
  /** Max in-flight allowed for concurrency buckets. */
  maxInFlight: number;
  /** TTL for in-flight slots in seconds. */
  ttlSeconds?: number;
}

/**
 * Business hours policy for message dispatch scheduling.
 * When enabled, messages are only dispatched during tenant work hours.
 */
export interface MessageReliabilityBusinessHoursPolicy {
  /** Enable business hours checking. Uses tenant SchedulingSettings. */
  enabled: boolean;
  /** Optional timezone override (defaults to tenant timezone). */
  timezone?: string;
}

export type MessageReliabilityRetryPolicy =
  | { kind: 'unlimited' }
  | { kind: 'limited'; maxAttempts: number; disposition: 'failed' | 'parked' };

export interface MessageReliabilityProcessConfig {
  process: MessageReliabilityProcessName;
  broker: MessageBroker;
  inbox: boolean;
  outbox: boolean;
  ordered: boolean;
  concurrency: number;
  subjects?: string[];
  queues?: string[];
  retention?: MessageReliabilityRetentionConfig;
  policyId?: string;
  bucketKeys?: string[];
  tokenCost?: number;
  concurrencyBucketKey?: string;
  rateBucketKey?: string;
  /** Rate limit configuration for token buckets. */
  rateLimit?: MessageReliabilityRateLimitConfig;
  /** Concurrency limit configuration for token buckets. */
  concurrencyLimit?: MessageReliabilityConcurrencyLimitConfig;
  /** Ingress admission control configuration. */
  ingressAdmission?: IngressAdmissionConfig;
  /** Business hours policy - only dispatch during work hours. */
  businessHours?: MessageReliabilityBusinessHoursPolicy;
  /**
   * Which application(s) run the inbox/outbox dispatcher for this process.
   *
   * - omitted → defaults to ApplicationTokens.JMS (the standard tenant app).
   * - single ApplicationTokens → only that app dispatches.
   * - array of ApplicationTokens → any listed app dispatches.
   *
   * The dispatcher reads process.env.APP (resolved to ApplicationTokens) at
   * runtime and skips processes whose scheduleOn does not include it.
   * This lets every container import MessageReliabilityModule uniformly
   * while ensuring each process is only swept by the app that owns it.
   */
  scheduleOn?: ApplicationTokens | readonly ApplicationTokens[];

  /** Retry policy for automatic retries. Defaults to unlimited. */
  retryPolicy?: MessageReliabilityRetryPolicy;
  /**
   * Defer delivery status to downstream worker.
   * When true:
   * - Includes `_outboxId` in payload when publishing to broker
   * - Marks message as `Processing` instead of `Delivered`
   * - Downstream worker is responsible for final status update (Delivered/Failed/Parked)
   * Use for processes where broker publish is intermediate (e.g., webhook HTTP delivery).
   */
  deferDeliveryStatus?: boolean;
  /** Fence late publisher acknowledgements and retry deferred work after a publisher/consumer crash. Opt-in only. */
  recoverDeferredDelivery?: boolean;
  /**
   * Lock TTL for message processing in milliseconds.
   * Defaults to MessageReliabilityDefaults.lockTtlMs.
   */
  lockTtlMs?: number;
  /**
   * Messages drained per partition per dispatcher sweep.
   * Defaults to the box default (MessageReliabilityDefaults.inboxBatchSize / outboxBatchSize).
   */
  batchSize?: number;
  /**
   * Ack timeout for deferred delivery in milliseconds.
   * When exceeded, messages are parked with ACK_TIMEOUT.
   * Defaults to MessageReliabilityDefaults.deferDeliveryAckTimeoutMs.
   */
  deferDeliveryAckTimeoutMs?: number;
  /**
   * Per-message expiration applied when publishing to RabbitMQ, in milliseconds.
   * A message still waiting in the queue after this long is dropped by the broker
   * (dead-lettered where the queue declares a dead-letter exchange), so work that
   * nobody picked up in time is never started late. Absent means no expiration,
   * which leaves every process that does not set it unaffected.
   */
  messageTtlMs?: number;
  /**
   * When true, RabbitMQ publishes wait for the broker's confirm (bounded by a timeout) before the
   * record moves on, so a rejected or unroutable message is retried instead of lost. The consumer
   * can finish before that confirm arrives, so the publisher's own settlement writes are fenced by
   * its publishing claim and never overwrite the consumer's outcome. Absent keeps the existing
   * unconfirmed publish.
   */
  confirmPublish?: boolean;
  /**
   * Builds the RabbitMQ copy of a deferred delivery at publish time, for data that must never be
   * stored on the outbox row (such as a short-lived credential) or that depends on the publish
   * moment. Throwing an error whose message starts with `4xx` parks the record. Processes that set
   * it also get `publishedAt` on their delivery envelope. Absent publishes the stored payload.
   */
  preparePublish?: (message: OutboxMessage, context: OutboxPublishContext) => OutboxPublishPreparation;
  /**
   * When true, terminal failures (Parked / Cancelled-with-error) of this process
   * do NOT dispatch admin inbox notifications. Use for internal pipelines that
   * have their own reconciliation / self-healing mechanism (e.g. vector sync
   * reconciler) where a transient failure is not actionable by an admin.
   *
   * This flag only affects notification dispatch. Other outbox behaviour is
   * unchanged: rows still appear in the Outbox admin console, retry/backoff
   * still applies, logger warnings still fire.
   *
   * Default: false (process failures notify admins — safe default preserves
   * behaviour for processes registered before this flag was introduced).
   */
  internal?: boolean;
}

/** What the outbox dispatcher hands a process's `preparePublish` hook. */
export interface OutboxPublishContext {
  /** The publish moment; the same value is stamped on the delivery envelope as `publishedAt`. */
  publishedAt: Date;
  configuration: Configuration;
}

/** The broker copy a `preparePublish` hook returns. */
export interface OutboxPublishPreparation {
  payload: Record<string, unknown>;
  /** Caps this publish's broker expiry below the process `messageTtlMs`. */
  expiresInMs?: number;
}
